'use client';

import { Locale, locales } from '@/i18n/locales';
import { usePathname, useRouter } from '@/i18n/navigation';
import { HTMLProps } from '@/lib/util/types';
import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useLocale, useTranslations } from 'next-intl';
import React, { CSSProperties, useId, useMemo, useState } from 'react';
import styles from './LanguageSelector.module.scss';

export type LanguageSelectorProps = {
  anchor?: `${'top' | 'bottom'} ${'left' | 'center' | 'right'}`;
  width?: CSSProperties['width'];
} & HTMLProps<HTMLDivElement>;

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  anchor = 'top left',
  width,
  ...props
}) => {
  const currentLocale = useLocale();
  const t = useTranslations('layout.language-selector.language-names');
  const selectId = useId();
  const dropdownId = useId();

  const pathname = usePathname();
  const router = useRouter();

  const vAlign = anchor.split(' ')[0] as 'top' | 'bottom';
  const hAlign = anchor.split(' ')[1] as 'left' | 'center' | 'right';

  const [open, setOpen] = useState(false);

  const setLocale = (locale: Locale) => {
    router.push(pathname, { locale });
    if (typeof umami !== 'undefined') {
      umami.track('switch-locale', { locale });
    }
  };
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLocale = e.target.value as Locale;
    setLocale(newLocale);
  };

  const options = locales.map((locale) => ({
    value: locale,
    inputLabel: locale.toUpperCase(),
    optionLabel: t(locale),
  }));

  const currentOption = useMemo(
    () => options.find((option) => option.value === currentLocale),
    [options, currentLocale]
  );

  const handleLabelClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    setOpen(true);
  };

  const handleOptionClick = (value: Locale) => (e: React.MouseEvent<HTMLSpanElement>) => {
    setLocale(value);
    setOpen(false);
  };

  return (
    <div
      className={styles.base}
      aria-haspopup="listbox"
      role="combobox"
      aria-expanded={open}
      aria-controls={dropdownId}
      {...props}
    >
      <label htmlFor={selectId} className={clsx(styles.inputLabel, { [styles.active]: open })}>
        <button role="button" className={styles.toggleButton} onClick={handleLabelClick}>
          <span>{currentOption?.inputLabel}</span>
          <span>
            <ChevronDown size="1.2em" />
          </span>
        </button>
      </label>
      <AnimatePresence>
        {open && (
          <>
            <div className={styles.backdrop} onClick={() => setOpen(false)} />
            <motion.ul
              id={dropdownId}
              role="listbox"
              className={styles.dropdown}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              style={{
                ...(vAlign === 'bottom'
                  ? { bottom: '100%', marginBottom: 8, justifyContent: 'flex-end' }
                  : { top: '100%', marginTop: 8 }),
                ...(hAlign === 'center'
                  ? { left: '50%', transform: 'translateX(-50%)' }
                  : hAlign === 'right'
                    ? { right: 0 }
                    : { left: 0 }),
                width,
              }}
            >
              {options.map(({ value, optionLabel }) => (
                <li
                  role="option"
                  aria-selected={value === currentLocale}
                  value={value}
                  key={value}
                  className={clsx(styles.option, { [styles.selected]: value === currentLocale })}
                  onClick={handleOptionClick(value)}
                >
                  {optionLabel}
                </li>
              ))}
            </motion.ul>
          </>
        )}
      </AnimatePresence>
      <select
        id={selectId}
        className={styles.selectInput}
        value={currentLocale}
        onChange={handleSelectChange}
      >
        {options.map(({ value, optionLabel }) => (
          <option key={value} value={value}>
            {optionLabel}
          </option>
        ))}
      </select>
    </div>
  );
};

export default LanguageSelector;
